import { useTranslation } from "react-i18next";

import { isContactEditClosedError } from "./api/contactEditClosed";
import { isContactEditNotAllowedError } from "./api/contactEditNotAllowed";
import { useUpdateContact } from "./api/useUpdateContact";
import { AbandonedScreen } from "./components/AbandonedScreen";
import { ContactEditClosedScreen } from "./components/ContactEditClosedScreen";
import { ContactEditForm } from "./components/ContactEditForm";
import { useContactEditSession } from "./hooks/useContactEditSession";

/**
 * ContactEditPage — 本人による連絡先修正画面のエントリポイント (ADR-0041)。
 *
 * URL: /c/:campaignRecordId/:respondentRecordId
 * `?t=<token>` はサーバ (backend/handlers/contactedit_verify.go) が検証しセッション Cookie を
 * 発行した上で本 URL へ 303 リダイレクトするため、本画面には token が渡ってこない。
 */
export function ContactEditPage() {
  const { t } = useTranslation("contactEdit");
  const { session } = useContactEditSession();
  const mutation = useUpdateContact();

  if (session.status === "loading") {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10">
        <p className="text-sm text-muted-foreground">{t("session.loading")}</p>
      </main>
    );
  }

  // 未受検 (abandoned) にされた respondent (ADR-0054 Decision 3)。verify 経路
  // (session.status==="abandoned") とセッション確立後に未受検にされて保存が拒否される経路
  // (isContactEditNotAllowedError) の両方をここでまとめて倒す。判定順序が重要:
  // isContactEditClosedError より先に判定しないと、両者とも 409 のため後者に握りつぶされる。
  if (session.status === "abandoned" || isContactEditNotAllowedError(mutation.error)) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10">
        <AbandonedScreen />
      </main>
    );
  }

  // 保存時の 409 も受付期限超過 (ADR-0041 Decision 5-1) であり、再試行しても成功しない。
  // 「通信状況を確認して再試行」の汎用エラーではなく、verify 経路と同じ受付終了画面に倒す。
  if (session.status === "closed" || isContactEditClosedError(mutation.error)) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10">
        <ContactEditClosedScreen />
      </main>
    );
  }

  if (session.status === "invalid") {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10">
        <section className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="text-base font-semibold">{t("session.invalidTitle")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("session.invalidDescription")}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-6 py-10">
      <header className="mb-10 border-b border-border pb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{session.session.companyName}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("page.description")}</p>
      </header>

      <ContactEditForm session={session.session} mutation={mutation} />
    </main>
  );
}
